// ONE PEOPLE PICKER for every assign / responsible / recipient field.
//
// Owner, 2026-10-15: "everywhere we can assign or recepient, the dropdown
// now needs to show all partners, their discipline on the partner header,
// their users, supervisor and normal users".
//
// A grouped antd Select: the header is the company with its trades ("BES -
// Electrical"), the people under it each carry a small tag saying what they
// are (Supervisor / User / Employee / Executive) and, when it matters, "No
// account", "Paused" or "2nd company". Search matches name, email, company
// and trade. The options come from peoplePickerOptions, which is where the
// rules live; this file only draws them.
//
// The value shape stays the FIELD's (see peoplePickerOptions): pass
// personValue / companyValue / noAccount / executives, and any antd Select
// prop (mode, value, onChange, placeholder, tagRender...) passes straight
// through. A caller that already holds the roster passes `roster`; one that
// does not passes `projectId` and the roster is fetched (and shared for a
// minute between every picker on the page).
import React, { useEffect, useMemo, useState } from "react";
import { Select, Tag, Tooltip } from "antd";
import { getParticipantsRoster } from "api/participants/participantsApi";
import { peoplePickerOptions, filterPeopleOption, KIND_LABEL, groupLabel } from "./peoplePickerOptions";

const KIND_COLOR = { supervisor: "blue", user: "default", employee: "default", executive: "purple" };

// One fetch per project per minute, shared by every picker that asks.
const cache = new Map();
export function loadRoster(projectId) {
  const key = String(projectId || "");
  if (!key) return Promise.resolve([]);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < 60000) return hit.promise;
  const promise = Promise.resolve(getParticipantsRoster(key))
    .then((r) => (Array.isArray(r) ? r : []))
    .catch(() => { cache.delete(key); return []; });
  cache.set(key, { at: Date.now(), promise });
  return promise;
}
export function clearRosterCache() { cache.clear(); }

/** The roster for `projectId` (or the one passed in), as state. */
export function useRoster(projectId, roster) {
  const [loaded, setLoaded] = useState(Array.isArray(roster) ? roster : []);
  useEffect(() => {
    if (Array.isArray(roster)) { setLoaded(roster); return undefined; }
    let alive = true;
    loadRoster(projectId).then((r) => { if (alive) setLoaded(r); });
    return () => { alive = false; };
  }, [projectId, roster]);
  return loaded;
}

export function PersonRow({ person, label, reason, isCompany, note }) {
  if (isCompany) return <span style={{ fontWeight: 500 }}>{label}</span>;
  if (!person) return reason ? <Tooltip title={reason}><span style={{ color: "#8c8c8c" }}>{label}</span></Tooltip> : <span>{label}</span>;
  // ONE LINE, ALWAYS (owner, 2026-10-16: the recipients list "is not working
  // as good as the responsible when we scroll"). The dropdown's list is
  // virtual and sized for one-line rows; a row that wrapped (a long email
  // plus "- no account yet, will get a link") was two or three lines tall,
  // and the list lost rows as it scrolled. Long text is cut with "..." and
  // the whole row is in the hover.
  const full = [person.name || person.email, person.email !== person.name ? person.email : "", person.jobTitle, note].filter(Boolean).join(" · ");
  const CUT = { overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 };
  const row = (
    <span title={reason ? undefined : full} style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap", whiteSpace: "nowrap", overflow: "hidden", minWidth: 0 }}>
      <span style={{ ...CUT, flexShrink: 0, maxWidth: "45%" }}>{person.name || person.email}</span>
      {person.email && person.email !== person.name ? (
        <span style={{ color: "#8c8c8c", fontSize: 12, ...CUT }}>{person.email}</span>
      ) : null}
      {person.kind ? (
        <Tag color={KIND_COLOR[person.kind]} style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}>
          {KIND_LABEL[person.kind] || person.kind}
        </Tag>
      ) : null}
      {person.jobTitle ? <span style={{ color: "#8c8c8c", fontSize: 12, ...CUT }}>{person.jobTitle}</span> : null}
      {note ? <span style={{ color: "#8c8c8c", fontSize: 12, ...CUT }}>- {note}</span> : null}
      {person.needsEmail ? <Tag color="gold" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}>Needs an email</Tag> : null}
      {!person.hasAccount && !person.needsEmail ? <Tag style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}>No account</Tag> : null}
      {person.paused ? <Tag color="orange" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}>Paused</Tag> : null}
      {person.secondMembership ? <Tag style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: "16px" }}>2nd company</Tag> : null}
    </span>
  );
  return reason ? <Tooltip title={reason}><span>{row}</span></Tooltip> : row;
}

/**
 * The row renderer, for a page that keeps its own Select (a custom
 * tagRender, a table cell) but shows the shared options:
 *   <Select options={peoplePickerOptions(roster, ...)} filterOption={filterPeopleOption}
 *           optionRender={renderPeopleOption} />
 */
export const renderPeopleOption = (opt) => (
  <PersonRow
    person={opt && opt.data && opt.data.person}
    label={opt && opt.data && opt.data.label}
    reason={opt && opt.data && opt.data.reason}
    isCompany={opt && opt.data && opt.data.isCompany}
    note={opt && opt.data && opt.data.note}
  />
);

/**
 * The "+ N ..." of a folded multi-select, with everyone it hides in the hover:
 * name, email and company (owner, 2026-10-16: "hovering over the +X number
 * ... should show full names and emails and contractors in tooltip for both
 * responsible and recipient"). A typed address nobody on the roster has is
 * listed as typed.
 *   <Select maxTagCount="responsive" maxTagPlaceholder={peopleTagPlaceholder(options)} />
 */
export function peopleTagPlaceholder(options) {
  const byValue = new Map();
  // Grouped (company > people) or flat - both are read.
  (options || []).forEach((g) => (g && Array.isArray(g.options) ? g.options : [g]).forEach((o) => {
    if (o && o.value != null) byValue.set(String(o.value), o);
  }));
  const lineOf = (v, shown) => {
    const o = byValue.get(String(v));
    if (!o) return { who: typeof shown === "string" && shown ? shown : String(v), email: "", company: "" };
    if (o.isCompany) return { who: String(o.label || v), email: "", company: "" };
    const p = o.person || {};
    return {
      who: p.name || p.email || String(o.label || v),
      email: p.email && p.email !== p.name ? p.email : "",
      company: o.group ? groupLabel(o.group) : (p.companyName || o.companyName || ""),
    };
  };
  const placeholder = (omitted) => {
    const list = (omitted || []).map((x) => (x && typeof x === "object" ? lineOf(x.value, x.label) : lineOf(x)));
    return (
      <Tooltip
        overlayStyle={{ maxWidth: 460 }}
        title={(
          <div data-testid="people-more" style={{ maxHeight: 320, overflowY: "auto" }}>
            {list.map((l, i) => (
              <div key={i} style={{ padding: "2px 0", lineHeight: "16px" }}>
                <div>{l.who}{l.email ? <span style={{ opacity: 0.75 }}> · {l.email}</span> : null}</div>
                {l.company ? <div style={{ fontSize: 11, opacity: 0.7 }}>{l.company}</div> : null}
              </div>
            ))}
          </div>
        )}
      >
        <span>+ {list.length} ...</span>
      </Tooltip>
    );
  };
  return placeholder;
}

export { peoplePickerOptions, filterPeopleOption };

export default function PeoplePicker({
  roster,
  projectId,
  personValue,
  companyValue,
  noAccount,
  executives,
  note,
  companyOptionLabel,
  extraGroups,
  ...selectProps
}) {
  const data = useRoster(projectId, roster);
  const options = useMemo(() => {
    const groups = peoplePickerOptions(data, { personValue, companyValue, noAccount, executives, note, companyOptionLabel });
    return groups.concat((extraGroups || []).filter((g) => g && g.options && g.options.length));
  }, [data, personValue, companyValue, noAccount, executives, note, companyOptionLabel, extraGroups]);

  return (
    <Select
      showSearch
      allowClear
      filterOption={filterPeopleOption}
      optionRender={renderPeopleOption}
      style={{ width: "100%" }}
      {...(selectProps.maxTagCount != null ? { maxTagPlaceholder: peopleTagPlaceholder(options) } : {})}
      {...selectProps}
      options={options}
    />
  );
}
